import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
  type UseControllerProps
} from 'react-hook-form';
import { useFieldErrorMessage } from './useFieldErrorMessage';
import { FormFieldInput, type FormFieldInputProps } from '@/components/form/FormFieldInput';

export interface FormInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<FormFieldInputProps, 'name' | 'value' | 'onChange' | 'error'> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Default value for the field
   */
  defaultValue?: string;

  /**
   * Field-level validation rules. Ignored when the form runs a resolver, so
   * this is for the schema-less forms only.
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];
}

/**
 * React Hook Form adapter for FormFieldInput
 * Automatically handles form state, validation, and errors
 *
 * @example
 * ```tsx
 * const { control } = useForm<FormData>();
 *
 * <FormInput
 *   control={control}
 *   name="email"
 *   label="Email"
 *   type="email"
 *   required
 * />
 * ```
 */
export function FormInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ control, name, defaultValue, rules, ...fieldProps }: FormInputProps<TFieldValues, TName>) {
  const fieldErrorMessage = useFieldErrorMessage();

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as never}
      rules={rules}
      render={({ field, fieldState }) => {
        const errorMessage = fieldErrorMessage(fieldState.error);

        return <FormFieldInput {...field} {...fieldProps} error={errorMessage} />;
      }}
    />
  );
}

FormInput.displayName = 'FormInput';
